import React, { forwardRef, useState, useId } from 'react';
import { clsx } from 'clsx';
import { designTokens } from '../../styles/design-tokens';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'borderless';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onClear?: () => void;
  showClearButton?: boolean;
  isLoading?: boolean;
  success?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  hint,
  size = 'md',
  variant = 'default',
  leftIcon,
  rightIcon,
  onClear,
  showClearButton = false,
  isLoading = false,
  success = false,
  className,
  disabled,
  required,
  id: providedId,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = props.value !== undefined ? String(props.value).length > 0 : undefined;

  // Size specifications ensuring touch-friendly targets
  const sizeClasses = {
    sm: {
      input: 'h-8 text-sm',
      padding: leftIcon ? 'pl-8 pr-3' : 'px-3',
      icon: 'h-4 w-4',
      iconLeft: 'left-2',
      iconRight: 'right-2',
    },
    md: {
      input: 'h-10 text-base',
      padding: leftIcon ? 'pl-10 pr-4' : 'px-4',
      icon: 'h-5 w-5',
      iconLeft: 'left-3',
      iconRight: 'right-3',
    },
    lg: {
      input: 'h-12 text-lg', // 48px - iOS HIG minimum
      padding: leftIcon ? 'pl-12 pr-6' : 'px-6',
      icon: 'h-6 w-6',
      iconLeft: 'left-4',
      iconRight: 'right-4',
    },
  };

  const variantClasses = {
    default: clsx(
      'border',
      error ? 'border-red-500 focus:ring-red-500' : 
      success ? 'border-green-500 focus:ring-green-500' :
      'border-gray-300 focus:border-[#4B5FDC] focus:ring-[#4B5FDC]'
    ),
    filled: clsx(
      'bg-gray-100 border-transparent',
      error ? 'focus:bg-white focus:border-red-500 focus:ring-red-500' :
      success ? 'focus:bg-white focus:border-green-500 focus:ring-green-500' :
      'focus:bg-white focus:border-[#4B5FDC] focus:ring-[#4B5FDC]'
    ),
    borderless: 'border-0 border-b-2 rounded-none px-0 focus:ring-0',
  };

  const baseInputClasses = clsx(
    'w-full rounded-lg transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'placeholder:text-gray-400',
  );

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    props.onBlur?.(e);
  };

  const showClear = showClearButton && hasValue && !disabled && !isLoading && onClear;

  return (
    <div className={clsx('w-full', className)}>
      {label && (
        <label
          htmlFor={id}
          className={clsx(
            'block mb-1.5 text-sm font-medium transition-colors',
            error ? 'text-red-700' :
            success ? 'text-green-700' :
            'text-gray-700'
          )}
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        {/* Left Icon */}
        {leftIcon && (
          <div className={clsx(
            'absolute top-1/2 -translate-y-1/2 pointer-events-none',
            sizeClasses[size].iconLeft,
            error ? 'text-red-500' :
            success ? 'text-green-500' :
            'text-gray-400'
          )}>
            <span className={sizeClasses[size].icon}>{leftIcon}</span>
          </div>
        )}

        {/* Input Field */}
        <input
          ref={ref}
          id={id}
          disabled={disabled || isLoading}
          className={clsx(
            baseInputClasses,
            sizeClasses[size].input,
            sizeClasses[size].padding,
            variantClasses[variant],
            rightIcon || showClear || isLoading ? 'pr-10' : '',
            className
          )}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-invalid={!!error}
          aria-describedby={
            error ? `${id}-error` : 
            hint ? `${id}-hint` : 
            undefined
          }
          {...props}
        />

        {/* Right Icon / Clear Button / Loading */}
        {(rightIcon || showClear || isLoading) && (
          <div className={clsx(
            'absolute top-1/2 -translate-y-1/2',
            sizeClasses[size].iconRight,
            error ? 'text-red-500' :
            success ? 'text-green-500' :
            'text-gray-400'
          )}>
            {isLoading ? (
              <svg
                className={clsx('animate-spin', sizeClasses[size].icon)}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            ) : showClear ? (
              <button
                type="button"
                onClick={onClear}
                className={clsx(
                  'text-gray-400 hover:text-gray-600 transition-colors',
                  sizeClasses[size].icon
                )}
                aria-label="Clear input"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            ) : rightIcon ? (
              <span className={sizeClasses[size].icon}>{rightIcon}</span>
            ) : null}
          </div>
        )}

        {/* Success Checkmark Animation */}
        {success && !error && !isLoading && (
          <div className={clsx(
            'absolute top-1/2 -translate-y-1/2 pointer-events-none animate-formSuccess',
            rightIcon || showClear ? 'right-10' : sizeClasses[size].iconRight
          )}>
            <svg
              className={clsx('text-green-500', sizeClasses[size].icon)}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Hint Text */}
      {hint && !error && (
        <p id={`${id}-hint`} className="mt-1.5 text-sm text-gray-500">
          {hint}
        </p>
      )}

      {/* Error Message */}
      {error && (
        <p
          id={`${id}-error`}
          role="alert"
          aria-live="polite"
          className="mt-1.5 text-sm text-red-600 animate-slideUp"
        >
          {error}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

// Specialized input variants
export interface FloatingLabelInputProps extends InputProps {
  label: string;
}

export const FloatingLabelInput = forwardRef<HTMLInputElement, FloatingLabelInputProps>(({
  label,
  size = 'md',
  className,
  ...props
}, ref) => {
  const [hasValue, setHasValue] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasValue(e.target.value.length > 0);
    props.onChange?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    props.onBlur?.(e);
  };

  const shouldFloat = hasValue || isFocused;

  return (
    <div className={clsx('relative', className)}>
      <Input
        ref={ref}
        size={size}
        className="pt-5 pb-1"
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder=" "
        {...props}
      />
      <label
        className={clsx(
          'absolute left-4 transition-all duration-200 pointer-events-none',
          shouldFloat ? 'top-1 text-xs text-gray-600' : 'top-3 text-base text-gray-400',
          props.error && 'text-red-500'
        )}
      >
        {label}
        {props.required && <span className="ml-1 text-red-500">*</span>}
      </label>
    </div>
  );
});

FloatingLabelInput.displayName = 'FloatingLabelInput';